import PropTypes from 'prop-types';
import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useMutation } from '@apollo/client';

import { Map, List, Iterable } from 'immutable';
import isEmpty       from 'lodash/isEmpty';

import Alert          from '@mui/material/Alert';
import Button         from '@mui/material/Button';
import LoadingButton  from '@mui/lab/LoadingButton';
import Grid           from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import Typography     from '@mui/material/Typography';
import Link           from '@mui/material/Link';
import Linkify        from 'components/Generic/Linkify';

import ChevronLeft  from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';

import { questionTypeSelector }                        from 'helpers/static';
import { CANDIDATE_APPLICATION_PROMPT_RESPONSE_INPUT } from 'queries/prompt/create';

import useBlobRecord from 'helpers/direct_upload/blob_record';

import debounce      from 'helpers/debounce';
import { Display }   from 'components';
import ApolloWrapper from 'pages/LandingPage/ApolloWrapper';
import { clearItem } from 'helpers/local_storage';

const FILE_TYPES = ['audio', 'video', 'file_upload', 'picture'];

const MAX_ANSWERS_PER_REQUEST = 10; // API Max per request is 100 but 10 to make it faster (Long Form only, will autosave when 10 answers are in queue)

const TALKSCORE_TYPE_MIN_LENGTH_REQURED = ['open_ended', 'listen_and_speak'];
const TALKSCORE_MIN_LENGTH = 30;
const TALKSCORE_MAX_LENGTH = 60;
const RECORDING_MIN_LENGHT = 0;
const RECORDING_MAX_LENGHT = 120;

function Answers({ application, beDone, isLongForm, isAlwaysSubmittable, checkFollowups, refetched, allowLandingPageResponseResubmit, country, answerCountAutosave = MAX_ANSWERS_PER_REQUEST }) {
  const [state, setState] = useState(new Map());

  const stateApplication   = state.get('stateApplication', new Map(application));
  const questions          = state.get('questions', new List(stateApplication.get('responses').filter((response) => isEmpty(response.get('value') || response.get('docs') || response.get('url')))));
  const questionIndex      = state.get('questionIndex', 0);
  const responses          = state.get('responses', new List());
  const responsesToPersist = state.get('responsesToPersist', new Set());
  const progress           = state.get('progress', 0);
  const responsesAnswered  = state.get('responsesAnswered', 0);
  const isSubmittable      = state.get('isSubmittable', false);
  const forceSave          = state.get('forceSave', null);

  const fromSubmit         = state.get('fromSubmit', false);
  const pendingResponse    = state.get('pendingResponse', null); // Handle multiple answers?
  const blobsToUpload      = state.get('blobsToUpload', []);
  const uploadingBlobs     = state.get('uploadingBlobs', false);
  const error              = state.get('error', undefined);

  const [submitResponses, returnResponse] = useMutation(CANDIDATE_APPLICATION_PROMPT_RESPONSE_INPUT);
  const [, directUpload, directUploadError] = useBlobRecord(blobsToUpload, 'response');

  const questiontypes = questions.filter((q) => q.getIn(['prompt', 'typeName']) === 'Question');

  const singleQuestionRequired = useMemo(() => {
    return questiontypes.some(
      (question) => {
        const talkscoreEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score']);
        const talkScoreTimerEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_timer_enabled']);
        const talkScoreTimer = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_timer']);

        return talkscoreEnabled && talkScoreTimerEnabled && talkScoreTimer;
      }
    );
  }, [questiontypes]);

  const singleQuestionMode = useMemo(() => {
    return singleQuestionRequired || !isLongForm;
  }, [singleQuestionRequired, isLongForm]);

  const showBackButton = useMemo(() => {
    return singleQuestionRequired ? false : allowLandingPageResponseResubmit;
  }, [singleQuestionRequired, allowLandingPageResponseResubmit]);

  useEffect(() => {
    if (returnResponse.data === undefined) return;

    if (fromSubmit) {
      checkFollowups(); // fromSubmit prevents that the last answer autosaves and trigger checkFollowups()
    }
  }, [returnResponse]);

  useEffect(() => {
    if (!forceSave) return;

    debounce(() => {
      if (questionIndex + 1 >= questiontypes.size) {
        onSubmit();
      } else {
        setState(state.set('questionIndex', questionIndex + 1));
      }
    }, 100, 'submit');
  }, [forceSave]);

  useEffect(() => {
    if (refetched) {
      setQuestionIndex(0);
    }
  }, [refetched]);

  useEffect(() => {
    // below is the computation for progress (percentage) by
    // determining the current question the candidate is seeing
    // setState( state.set('progress', Math.round((questionIndex/questions.size) * 100)) );

    if (!isLongForm && !isAlwaysSubmittable) {
      save();
    }
  }, [questionIndex]);

  useEffect(() => {
    if (directUploadError === undefined) return;

    setState(state.set('error', t('eos.landing_page.text.upload_failed')).set('uploadingBlobs', false));
  }, [directUploadError]);

  useEffect(() => {
    if (isEmpty(directUpload)) return;

    let response = new Map(pendingResponse);

    if (response.get('blob')) {
      response = response.set('file', directUpload[0].signedBlobId);
    }

    addResponse(response.get('promptId'), response);
  }, [directUpload]);

  useEffect(() => {
    if (isEmpty(pendingResponse)) return;

    // Case where the user deleted a blob and then added a new file in the file upload is not yet considered
    const blobs = pendingResponse.get('blob');
    setState(state.set('blobsToUpload', blobs));
  }, [pendingResponse]);

  useEffect(() => {
    let submittable  = false;
    // isolate file questions. we will check if there is a blob file uploaded

    const fileQuestions   = questiontypes.filter((q) => FILE_TYPES.includes(q.getIn(['prompt', 'categoryName']))).map((q) => q.getIn(['prompt', 'id']));
    // isolate text questions
    const normalQuestions = questiontypes.filter((q) => !FILE_TYPES.includes(q.getIn(['prompt', 'categoryName']))).map((q) => q.getIn(['prompt', 'id']));

    // get all text responses which are answered
    const answeredNormals = responses.filter((res) => res.get('value') !== '' && normalQuestions.includes(res.get('promptId')));

    // get all file responses which are answered
    const answeredFiles   = responses.filter((res) =>
      (
        (
          (res.get('file') && res.get('file') !== '') ||
          (res.get('blobs') && res.get('blobs').length > 0 && res.get('blobs').every((blob) => blob.get('signedBlobId'))) ||
          res.get('value') !== ''
        ) &&
        fileQuestions.includes(res.get('promptId'))
      )
    );

    if (responses.size === questiontypes.size) {
      // if answered is equal to normal questions
      if ((answeredNormals.size + answeredFiles.size) === questiontypes.size) {
        submittable = true;
      }
    }

    setState(state.set('progress', Math.round(((answeredNormals.size + answeredFiles.size) / questiontypes.size) * 100))
      .set('isSubmittable', submittable)
      .set('responsesAnswered', (answeredNormals.size + answeredFiles.size)));
  }, [responses, forceSave]);

  useEffect(() => {
    if (forceSave || isSubmittable) return;

    if (responsesToPersist.size >= answerCountAutosave) save();
  }, [responses, responsesToPersist, forceSave, isSubmittable]);

  function setQuestionIndex(index) {
    if (index < 0 || index === questiontypes.size) return;
    setState(state.set('questionIndex', index).set('forceSave', null));
  }

  function setBlobName(blobs) {
    if (isEmpty(blobs)) return blobs;

    blobs.forEach((blob) => {
      const extension = blob.type.split('/').pop();
      blob.name = blob.name ? blob.name : `app${stateApplication.get('id')}-${Date.now()}.${extension}`;
    });

    return blobs;
  }

  function onChange(promptId, value) {
    const text   = value.get('text');
    const number = value.get('number');
    const blob   = setBlobName(value.get('blob'));
    const blobs  = value.get('blobs');
    const duration = value.get('duration');
    const incomplete = value.get('incomplete');
    const forced = value.get('forced');

    const geolocation = value.get('geolocation');

    const checks = [text, number, blob, blobs, geolocation];
    if (checks.every((check) => isEmpty(check))) {
      if (forced) {
        setState(state.set('forceSave', responses.find((res) => res.get('promptId') === promptId)));
        return;
      }

      removeResponse(promptId);
      return;
    }

    const question = questions.find((question) => question.getIn(['prompt', 'id']) === promptId);
    const questionType = question.getIn(['prompt', 'categoryName']);

    const response = new Map({
      applicationId: stateApplication.get('id'),
      value:         text || number,
      promptId,
      blob,
      blobs,
      geolocation,
      duration,
      incomplete,
      forced,
    });

    const toUploadFile = ['audio/text', 'audio', 'video', 'picture'].includes(questionType) && blob;

    if (toUploadFile) {
      setState(state.set('pendingResponse', response)
        .set('uploadingBlobs', true));
      return;
    }

    // Case when deleting a file in a file_upload input will not go through the above condition.
    addResponse(promptId, response);
  }

  function removeResponse(promptId) {
    const pidx = responses.findIndex((res) => res.get('promptId') === promptId);

    if (pidx >= 0) setState(state.set('responses', responses.delete(pidx)));
  }

  function addResponse(promptId, response) {
    const pidx = responses.findIndex((res) => res.get('promptId') === promptId);

    if (pidx === -1) {
      setState(state.set('responses', responses.push(response))
        .set('pendingResponse', null)
        .set('uploadingBlobs', false)
        .set('error', undefined)
        .set('responsesToPersist', !response.get('forced') ? responsesToPersist.add(promptId) : new Set())
        .set('forceSave', response.get('forced') ? response : null));

      return;
    }

    setState(state.setIn(['responses', pidx], response)
      .set('pendingResponse', null)
      .set('uploadingBlobs', false)
      .set('error', undefined)
      .set('responsesToPersist', !response.get('forced') ? responsesToPersist.add(promptId) : new Set())
      .set('forceSave', response.get('forced') ? response : null));
  }

  function onSubmit() {
    if (responsesToPersist.size > 0 || forceSave) {
      postResponses(getResponsesToPost(), true);

      questiontypes.forEach(
        (question) => clearItem(`${stateApplication.get('id')}-${question.getIn(['prompt', 'id'])}`)
      );

      // TODO: show the loading screen
      setState(state.set('fromSubmit', true)
        .set('responsesToPersist', new Set())
        .set('forceSave', null));
    } else {
      beDone();
    }
  }

  function save() {
    if ((responsesToPersist.size > 0 || forceSave) && !isSubmittable) postResponses(getResponsesToPost());

    setState(state.set('responsesToPersist', new Set()).set('forceSave', null));
  }

  // TODO: named params
  function postResponses(responses, fromsubmit = false) {
    if (!responses || responses.size === 0) return;

    submitResponses({
      variables:
      {
        response: responses,
      },
    });
  }

  function getResponsesToPost() {
    if (forceSave) return [parseResponse(forceSave).toJS()];

    return responses.filter((response) => !isEmpty(response) && responsesToPersist.has(response.get('promptId')))
      .map((response) => parseResponse(response)).toJS();
  }

  const getValueFromResponse = (response) => {
    if (isEmpty(response)) return null;

    return new Map({
      value: response.get('value'), blob: response.get('blob'), blobs: response.get('blobs'), geolocation: response.get('geolocation'), duration: response.get('duration'),
    }); // For inputs, the signedBlobId is not useful
  };

  const parseResponse = (response) => {
    // Signed blobs id are stored in the blobs property
    // We need to populate the docs field with only the signed blobs id
    let parsedResponse = new Map(response);

    if (parsedResponse.get('blobs')) {
      parsedResponse = parsedResponse.set('docs', parsedResponse.get('blobs').map((blob) => ({ file: blob.get('signedBlobId'), recognition: blob.get('recognition') })));
    }

    // Need to remove the blobs and blob property
    return parsedResponse.delete('blobs').delete('blob').delete('duration').delete('forced');
  };

  function retry() {
    setState(state.set('blobsToUpload', [...blobsToUpload]));
  }

  function getHardcodedInstructions(question) {
    if (isEmpty(question)) return null;

    if (question.getIn(['prompt', 'metadata', 'parameters', 'talk_score']) && question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_type']) !== 'listen_and_repeat') {
      return <Typography data-testid="answers-talkscore-instructions">{t('eos.landing_page.text.talkscore_instructions')}</Typography>;
    }

    return null;
  }

  const getAnswerComponentProps = (question) => {
    switch (question.getIn(['prompt', 'categoryName'])) {
      case 'number':
        return {
          minimum: question.getIn(['prompt', 'metadata', 'parameters', 'minimum']),
          maximum: question.getIn(['prompt', 'metadata', 'parameters', 'maximum']),
        };
      case 'dropdown':
        return {
          dropdownOptions: question.getIn(['prompt', 'metadata', 'parameters', 'dropdown_params']).split(','),
        };
      case 'multiple choice':
        return {
          options: new List([...question.getIn(['prompt', 'metadata', 'multiple_options'])]),
        };
      case 'audio':
      case 'video':
        const talkscoreEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score']);
        const talkScoreType = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_type']);
        const talkScoreTimerEnabled = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_timer_enabled']);
        const talkScoreTimer = question.getIn(['prompt', 'metadata', 'parameters', 'talk_score_timer']);

        return {
          min: talkscoreEnabled && TALKSCORE_TYPE_MIN_LENGTH_REQURED.includes(talkScoreType) ? TALKSCORE_MIN_LENGTH : RECORDING_MIN_LENGHT,
          max: talkscoreEnabled && TALKSCORE_TYPE_MIN_LENGTH_REQURED.includes(talkScoreType) ? TALKSCORE_MAX_LENGTH : RECORDING_MAX_LENGHT,
          timer: talkscoreEnabled && talkScoreTimerEnabled && talkScoreTimer ? talkScoreTimer : null,
        }
      case 'play_media':
        return {
          mediaUrl:    question.getIn(['prompt', 'metadata', 'media', 'url']),
          contentType: question.getIn(['prompt', 'metadata', 'media', 'content_type']),
          identifier:  `${application.get('id')}-${question.getIn(['prompt', 'id'])}`,
          replayable:  question.getIn(['prompt', 'metadata', 'parameters', 'replayable']),
        };
      case 'file_upload':
        return {
          country,
          documentName: question.getIn(['prompt', 'documentTag', 'name']),
          recognize: question.getIn(['prompt', 'documentTag', 'parameters', 'landingPageProcessable']),
        };
      default:
        return {};
    }
  };

  const getQuestionTitle = (question) => {
    switch (question.getIn(['prompt', 'categoryName'])) {
      case 'play_media':
        return t('eos.landing_page.text.play_media.instruction');
      default:
        return question.getIn(['prompt', 'title']);
    }
  };

  return (
    <ApolloWrapper token={application.bearerToken}>
      {
        // TODO: Skipping Prompt typename since we dont have a proper UI for it yet
        questiontypes.map((question, index) => {
          const response = responses.filter((res) => res.get('promptId') === question.getIn(['prompt', 'id'])).get(0);
          const questionType = question.getIn(['prompt', 'categoryName']);
          const AnswerComponent = questionTypeSelector(questionType);
          const title = getQuestionTitle(question);

          if ((singleQuestionMode && questionIndex !== index)
              || AnswerComponent === null) return null;

          const answerComponentProps = getAnswerComponentProps(question);

          return (
            <Grid container item key={question.getIn(['prompt', 'id'])} sx={{ mb: 5 }}>
              <Grid item xs={12} mb={2}>
                <Typography variant="body" sx={{ fontWeight: 'medium' }}>
                  <label data-testid="answers-question-title" htmlFor={`question-${question.getIn(['prompt', 'id'])}`} style={{ whiteSpace: 'pre-wrap' }}>
                    <Linkify>
                      {`${index + 1}. ${title}`}
                    </Linkify>
                  </label>
                </Typography>

                { getHardcodedInstructions(question) }

                <Display if={question.getIn(['prompt', 'documentSampleUrl'])}>
                  <Link href={question.getIn(['prompt', 'documentSampleUrl'])} underline="none" target="_blank" rel="noreferrer">
                    <Typography variant="p">
                      {t('eos.landing_page.label.view_sample')}
                    </Typography>
                  </Link>
                </Display>
                <Display if={question.getIn(['prompt', 'documentInstruction'])}>
                  { /* TODO: detect link strings */ }
                  <Typography variant="p" display="block" gutterBottom>
                    {t('eos.landing_page.text.instructions', { text: question.getIn(['prompt', 'documentInstruction']) })}
                  </Typography>
                </Display>
              </Grid>

              <Grid item xs={12}>
                {/* Would be nice to show a loading answer component instead of the answer component when the blobs are being uploaded */}
                <AnswerComponent
                  onChange={onChange}
                  name={question.getIn(['prompt', 'id'])}
                  id={`${stateApplication.get('id')}-${question.getIn(['prompt', 'id'])}`}
                  value={getValueFromResponse(response)}
                  disabled={(fromSubmit && returnResponse.loading) || uploadingBlobs}
                  {...answerComponentProps}
                />
              </Grid>
            </Grid>
          );
        })
      }

      <Display if={singleQuestionMode}>
        <Grid container item xs={12} spacing={1} sx={{ mt: 5 }}>
          <Grid item md={10} xs={8}>
            <Typography variant="body2" color="text.secondary">{t('eos.landing_page.text.question_progress', { current: responsesAnswered, size: questiontypes.size })}</Typography>
          </Grid>
          <Grid item xs={12}>
            <LinearProgress value={progress} color="primary" variant="determinate" sx={{ height: 10 }} />
          </Grid>
        </Grid>
        <Display if={questiontypes.size > 1}>
          <Grid container item xs={12} spacing={1} sx={{ mt: 2 }}>
            <Grid item xs={6}>
              <Display if={showBackButton}>
                <Button disabled={questionIndex === 0 || returnResponse.loading || uploadingBlobs} variant="contained" color="primary" size="small" onClick={() => setQuestionIndex(questionIndex - 1)} sx={{ mr: 1 }} data-testid="prev"><ChevronLeft fontSize="small" /></Button>
              </Display>
            </Grid>
            <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button disabled={(!isAlwaysSubmittable && questionIndex >= responsesAnswered) || questionIndex === (questiontypes.size - 1) || returnResponse.loading || uploadingBlobs} variant="contained" color="primary" size="small" onClick={() => setQuestionIndex(questionIndex + 1)} data-testid="next"><ChevronRight fontSize="small" /></Button>
            </Grid>
          </Grid>
        </Display>
      </Display>

      <Display if={error}>
        <Grid item xs={12} sx={{ display: 'flex', mt: 4, justifyContent: 'center' }}>
          <Alert severity="error">{error}</Alert>
          <Button variant="contained" color="primary" size="small" onClick={retry} data-testid="answers-index-retry-button">{t('eos.landing_page.label.retry')}</Button>
        </Grid>
      </Display>

      <Grid item xs={12} sx={{ display: 'flex', mt: 4, justifyContent: 'center' }}>
        <Display id="error" if={returnResponse.error && fromSubmit}>
          <Alert severity="error" sx={{ mr: 3 }}>{t('eos.landing_page.text.application_invalid')}</Alert>
        </Display>
        {/* TODO: display only if last question */}
        <LoadingButton disabled={!isSubmittable && !isAlwaysSubmittable} loading={returnResponse.loading || uploadingBlobs} disableElevation variant="contained" color="primary" size="large" onClick={() => onSubmit()}>{t('eos.landing_page.label.submit')}</LoadingButton>
      </Grid>
    </ApolloWrapper>
  );
}

Answers.defaultProps = {
  application: null,
  beDone:      null,
};

Answers.propTypes = {
  application: PropTypes.objectOf(Iterable),
  beDone:      PropTypes.func,
};

export default Answers;
